/* eslint-disable max-len */
import React, { useContext, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Bell, LogOut, Menu, User, ChevronDown } from 'lucide-react';
import { useHistory } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import UserContext from '../hooks/context/UserContext';
import useLogout from '../hooks/useLogout';
import { cn } from '../lib/utils';
import useNotification from '../sections/hooks/UseNotification';
import NotificationPopup from './components/NotificationPopup';

function Header({ title, onMenuClick, showMenuButton }) {
  const { user } = useContext(UserContext);
  const { logoutUser } = useLogout();
  const history = useHistory();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Notification hook
  const {
    headNotification,
    isModalOpened,
    notificationRef,
    isHead,
    onOpenModal,
    onCloseModal,
    handleStatusChange,
    pendingNotificationsCount,
  } = useNotification(user);

  const displayName = (() => {
    const first = user?.firstName?.trim();
    const last = user?.lastName?.trim();
    if (first && last) return `${first} ${last}`;
    if (first) return first;
    if (last) return last;
    return user?.email || 'User';
  })();

  const userInitials = (() => {
    const first = user?.firstName?.trim();
    const last = user?.lastName?.trim();
    if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
    if (first) return first.slice(0, 2).toUpperCase();
    if (last) return last.slice(0, 2).toUpperCase();
    return user?.email?.[0]?.toUpperCase() || 'U';
  })();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    setIsDropdownOpen(false);
    logoutUser();
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b bg-background px-6">
      {/* Mobile menu button */}
      {showMenuButton && (
        <Button variant="ghost" size="icon" className="lg:hidden" onClick={onMenuClick}>
          <Menu className="h-5 w-5" />
        </Button>
      )}

      {/* Page Title */}
      <div className="flex-1">
        <h1 className="text-xl font-semibold text-foreground">{title}</h1>
      </div>

      {/* Right side actions */}
      <div className="flex items-center gap-3">
        {/* Notifications - only show for HEAD role */}
        {isHead && (
          <div className="relative">
            <Button
              ref={notificationRef}
              variant="ghost"
              size="icon"
              className="relative"
              onClick={onOpenModal}
            >
              <Bell className="h-5 w-5" />
              {/* Notification badge */}
              {pendingNotificationsCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-medium text-destructive-foreground">
                  {pendingNotificationsCount > 9 ? '9+' : pendingNotificationsCount}
                </span>
              )}
            </Button>
            <NotificationPopup
              isOpen={isModalOpened}
              onClose={onCloseModal}
              notifications={headNotification}
              onStatusChange={handleStatusChange}
            />
          </div>
        )}

        {/* User Menu */}
        <div className="relative" ref={dropdownRef}>
          <Button
            variant="ghost"
            className="flex items-center gap-2 px-2"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <Avatar className="h-8 w-8">
              <AvatarImage src={user?.hydraProfileLink} alt={displayName} />
              <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                {userInitials}
              </AvatarFallback>
            </Avatar>
            <span className="hidden text-sm font-medium md:inline-block">{displayName}</span>
            <ChevronDown
              className={cn(
                'h-4 w-4 transition-transform duration-200',
                isDropdownOpen && 'rotate-180',
              )}
            />
          </Button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-56 rounded-md border bg-popover p-1 shadow-lg animate-in fade-in-0 zoom-in-95">
              <div className="px-3 py-2 border-b mb-1">
                <p className="text-sm font-medium">{displayName}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                onClick={() => {
                  setIsDropdownOpen(false);
                  history.push('/avatar');
                }}
              >
                <User className="h-4 w-4" />
                Profile
              </button>
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4" />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

Header.propTypes = {
  title: PropTypes.string,
  onMenuClick: PropTypes.func,
  showMenuButton: PropTypes.bool,
};

Header.defaultProps = {
  title: 'Dashboard',
  onMenuClick: () => {},
  showMenuButton: false,
};

export default Header;
